import { useEffect, useMemo, useRef, useState } from 'react';
import { apiError, getResearch, postResearch } from '../../lib/research';
import { chartAgentDisplay, freshChartEvidence, snapshotSignature } from './agentSignals';

const preferences = () => {
  try { const saved = JSON.parse(localStorage.getItem('research-agent-signal-visibility') || '{}'); return { vibe: saved.vibe !== false, technical: saved.technical !== false, risk: saved.risk !== false }; }
  catch { return { vibe: true, technical: true, risk: true }; }
};

export const useAgentSignals = (snapshot, onSnapshot, showByDefault = false) => {
  const [run, setRun] = useState(null), [busy, setBusy] = useState(false), [error, setError] = useState('');
  const [loading, setLoading] = useState(false), [visible, setVisible] = useState(() => showByDefault ? { vibe: true, technical: true, risk: true } : preferences());
  const lastIdentity = useRef(null);
  useEffect(() => {
    const id = snapshot ? `${snapshot.asset}:${snapshot.symbol}` : null;
    if (showByDefault && id && id !== lastIdentity.current) setVisible({ vibe: true, technical: true, risk: true });
    if (id) lastIdentity.current = id;
  }, [snapshot?.asset, snapshot?.symbol, showByDefault]);
  const controller = useRef(null), epoch = useRef(0), activeId = useRef(null), alive = useRef(true);
  const key = snapshotSignature(snapshot);
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; controller.current?.abort(); };
  }, []);
  useEffect(() => {
    const generation = ++epoch.current;
    controller.current?.abort(); controller.current = null;
    setRun(null); setBusy(false); setError('');
    if (!snapshot) return;
    const abort = new AbortController(); setLoading(true);
    getResearch('/research/signals', { params: { symbol: snapshot.symbol, asset: snapshot.asset, period: snapshot.period }, signal: abort.signal })
      .then(({ data }) => { if (alive.current && generation === epoch.current) setRun(data.find(r => snapshotSignature(r.snapshot) === key) || null); })
      .catch(err => { if (err.code !== 'ERR_CANCELED' && generation === epoch.current) setError('Saved agent signals could not be loaded.'); })
      .finally(() => { if (alive.current && generation === epoch.current) setLoading(false); });
    return () => abort.abort();
  }, [key]);
  const review = async () => {
    if (!snapshot || controller.current) return;
    const abort = new AbortController(), generation = epoch.current;
    controller.current = abort; activeId.current = null; setBusy(true); setError('');
    try {
      let current = run?.status === 'running' ? run : (await postResearch('/research/signals', {
        symbol: snapshot.symbol, asset: snapshot.asset, period: snapshot.period,
        expected_source_timestamp: snapshot.source_timestamp, expected_candle_time: snapshot.bars.at(-1).time,
      }, { signal: abort.signal })).data;
      activeId.current = current.id;
      if (snapshotSignature(current.snapshot) !== key) {
        await postResearch(`/research/signals/${current.id}/cancel`, {});
        onSnapshot(current.snapshot);
        throw new Error('Market evidence was refreshed. Review the updated chart, then analyze again.');
      }
      if (generation !== epoch.current) return;
      setRun(current);
      for (const agent of current.agents) {
        if (abort.signal.aborted || current.status !== 'running') break;
        if (['completed', 'unavailable'].includes(agent.status)) continue;
        current = (await postResearch(`/research/signals/${current.id}/agents/${agent.id}`, {}, { signal: abort.signal, timeout: 45000 })).data;
        if (alive.current && generation === epoch.current) setRun(current);
      }
    } catch (err) {
      if (alive.current && generation === epoch.current && !['CanceledError', 'AbortError'].includes(err.name)) setError(apiError(err));
    } finally {
      if (controller.current === abort) controller.current = null;
      if (alive.current && generation === epoch.current) setBusy(false);
    }
  };
  const stop = async () => {
    controller.current?.abort();
    if (!activeId.current) return;
    try { const { data } = await postResearch(`/research/signals/${activeId.current}/cancel`, {}); if (alive.current) setRun(data); }
    catch (err) { if (alive.current) setError(apiError(err)); }
  };
  const toggle = (id, checked) => setVisible(previous => {
    const next = { ...previous, [id]: checked };
    try { if (!showByDefault) localStorage.setItem('research-agent-signal-visibility', JSON.stringify(next)); } catch { /* Display preferences are optional. */ }
    return next;
  });
  const matched = run && snapshotSignature(run.snapshot) === key ? run : null;
  const display = useMemo(() => chartAgentDisplay(matched, snapshot, visible), [matched, snapshot, visible]);
  const markers = useMemo(() => display.filter(entry => entry.marker).map(entry => entry.marker), [display]);
  return { run: matched, busy, loading, error, visible, toggle, review, stop, fresh: freshChartEvidence(snapshot),
    canStop: Boolean(activeId.current), markers, display };
};